import { datadogRum } from '@datadog/browser-rum';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@salesforce/platform-sdk/ui-embedding';
// Ensures the OAuth redirect is caught on a fresh page load, not just mid-session.
import 'aws-amplify/auth/enable-oauth-listener';
import { MantineProvider } from '@mantine/core';
import { IconSettings, Settings } from '@salesforce/design-system-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import { App } from './App';
import { AuthGate } from './auth/AuthGate';
import { configureAmplify } from './config/amplify';
import { theme } from './theme';

declare const __APP_VERSION__: string;

configureAmplify();

if (import.meta.env.PROD) {
  datadogRum.init({
    allowedTracingUrls: [import.meta.env.VITE_API_BASE_URL],
    applicationId: import.meta.env.VITE_DD_APPLICATION_ID ?? 'placeholder',
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN ?? 'placeholder',
    defaultPrivacyLevel: 'mask-user-input',
    env: import.meta.env.VITE_DD_ENV ?? 'local',
    service: import.meta.env.VITE_DD_SERVICE ?? 'mkf',
    // Vite env vars are always strings at runtime — Number() converts correctly
    sessionReplaySampleRate: Number(import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE ?? '0'),
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: __APP_VERSION__,
  });

  datadogRum.startSessionReplayRecording();
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

Settings.setAppElement(rootElement);
createRoot(rootElement).render(
  <StrictMode>
    {/* No host shell provides a theme to sync with here (unlike the Pricing Engine reference app's
        HostServicesContext) — this app's own color-scheme is always light (see index.html). */}
    <MantineProvider defaultColorScheme="light" theme={theme}>
      <IconSettings iconPath="/assets/icons">
        <ErrorBoundary
          FallbackComponent={props => (
            <div>
              <h1>Oops! Something went wrong</h1>
              <p>{props.error?.toString()}</p>
            </div>
          )}
        >
          <AuthGate>
            <App />
          </AuthGate>
        </ErrorBoundary>
      </IconSettings>
    </MantineProvider>
  </StrictMode>,
);
